import { useState, FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ShieldCheck } from "lucide-react";
import { z } from "zod";
import { useAuth } from "@/contexts/AuthContext";
import { requestPasswordResetEverywhere } from "@/integrations/supabase/authProviders";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const schema = z.object({
  email: z.string().trim().email("Enter a valid email").max(255),
  password: z.string().min(1, "Password is required").max(72),
});

const BRAND = "#7C3AED";
const BRAND_DARK = "#6D28D9";

export default function Login() {
  const { user, loading, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSending, setForgotSending] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotError, setForgotError] = useState<string | null>(null);

  // Falls back to `/`, not `/dashboard`: `/` is the route that decides which
  // dashboard this operator's org runs (see src/lib/operatorHome.ts), and a
  // sign-in with no deep-link is the path most operators take. A deep-link
  // still wins — ProtectedRoute puts the original path in `state.from`.
  const from = (location.state as { from?: string } | null)?.from ?? "/";

  if (!loading && user) return <Navigate to={from} replace />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const parsed = schema.safeParse({ email, password });
    if (!parsed.success) {
      setError(parsed.error.issues[0].message);
      return;
    }
    setSubmitting(true);
    const { error: err } = await signIn(parsed.data.email, parsed.data.password);
    setSubmitting(false);
    if (err) {
      setError(err);
      return;
    }
    navigate(from, { replace: true });
  };

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-12rem)] px-4 py-10">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-[0_20px_60px_-20px_rgba(76,29,149,0.18)] p-8 sm:p-10">
          <div className="text-center space-y-2 mb-8">
            <h1 className="text-[28px] font-bold tracking-tight text-slate-900">Sign in</h1>
            <p className="text-sm text-slate-500">Use your DigaVouch credentials to continue.</p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                className="h-12 rounded-xl border-slate-200 bg-slate-50/60 text-slate-900 placeholder:text-slate-400 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-[#7C3AED]/30 focus-visible:border-[#7C3AED] transition-all"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                  Password
                </Label>
                <button
                  type="button"
                  onClick={() => {
                    setForgotEmail(email);
                    setForgotError(null);
                    setForgotSent(false);
                    setForgotOpen(true);
                  }}
                  className="text-xs font-semibold text-[#6D28D9] hover:text-[#5B21B6]"
                >
                  Forgot password?
                </button>
              </div>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="h-12 rounded-xl border-slate-200 bg-slate-50/60 text-slate-900 placeholder:text-slate-400 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-[#7C3AED]/30 focus-visible:border-[#7C3AED] transition-all"
              />
            </div>

            {error && (
              <Alert variant="destructive" className="rounded-xl">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              disabled={submitting || loading}
              className="w-full h-12 rounded-xl text-sm font-semibold text-white shadow-sm hover:shadow-lg hover:-translate-y-px transition-all border-0"
              style={{ background: `linear-gradient(135deg, ${BRAND}, ${BRAND_DARK})` }}
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <div className="mt-7 pt-6 border-t border-slate-100 flex items-center justify-center gap-2 text-xs text-slate-500">
            <ShieldCheck className="w-3.5 h-3.5 text-[#7C3AED]" />
            <span>Secured by DigaVouch identity infrastructure</span>
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-slate-500">
          Need access?{" "}
          <a href="https://digavouch.com/request-demo" className="font-semibold text-[#6D28D9] hover:text-[#5B21B6]">
            Request a pilot
          </a>
        </p>
      </motion.div>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reset your password</DialogTitle>
            <DialogDescription>
              Enter the email tied to your operator account. We'll send a secure link to choose a new password.
            </DialogDescription>
          </DialogHeader>
          {forgotSent ? (
            <Alert className="rounded-xl">
              <AlertDescription>
                If an account exists for <strong>{forgotEmail}</strong>, a reset link is on its way.
              </AlertDescription>
            </Alert>
          ) : (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setForgotError(null);
                const parsedEmail = z.string().trim().email().safeParse(forgotEmail);
                if (!parsedEmail.success) {
                  setForgotError("Enter a valid email");
                  return;
                }
                setForgotSending(true);
                // Every provider: the account lives in exactly one and
                // Supabase will not tell us which (anti-enumeration), so asking
                // only one silently no-ops for the other's users.
                const { error: err } = await requestPasswordResetEverywhere(
                  parsedEmail.data,
                  `${window.location.origin}/reset-password`,
                );
                setForgotSending(false);
                // Only a total failure surfaces. One provider erroring is
                // expected; every provider erroring means the mail genuinely
                // did not go, and silently showing success would leave someone
                // waiting for it.
                if (err) {
                  setForgotError(err);
                  return;
                }
                setForgotSent(true);
              }}
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="forgot-email" className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                  Email
                </Label>
                <Input
                  id="forgot-email"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  className="h-11 rounded-xl"
                />
              </div>
              {forgotError && (
                <Alert variant="destructive" className="rounded-xl">
                  <AlertDescription>{forgotError}</AlertDescription>
                </Alert>
              )}
              <DialogFooter>
                <Button
                  type="submit"
                  disabled={forgotSending}
                  className="w-full h-11 rounded-xl text-sm font-semibold text-white border-0"
                  style={{ background: `linear-gradient(135deg, ${BRAND}, ${BRAND_DARK})` }}
                >
                  {forgotSending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
